import CompanyController from "api/controllers/CompanyController";
import { IUsers } from "api/interfaces";
import CompanyModel from "api/models/CompanyModel";
import UserModel from "api/models/UserModel";
import CompanyService from "api/services/CompanyService";
import UserService from "api/services/UserService";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import { compareStrings, sleep } from "utils";
import { MessageStore, UserStore } from "./";

class Store {
  private observer: any = undefined;
  private _company?: CompanyModel = undefined;
  private _employeesList: Array<UserModel> = [];

  constructor() {
    makeAutoObservable(this);
  }

  get currentCompany() {
    return this._company;
  }

  get employeesList() {
    return this._employeesList;
  }

  reset() {
    this._company = undefined;
    this._employeesList = [];
  }

  subscribeCompany(onChange: (company: CompanyModel) => void) {
    this.observer = CompanyService.subscribeCompanySnapshotByUser((company) => {
      this._company = company;
      if (company) {
        onChange(company);
        const users = Object.keys(company.users);
        const user = users.find((us) => us === UserStore.currentUser?.uid);
        // Atualiza permissao do usuario atual
        if (user) {
          let access = company.users[user];
          UserStore.setCurrentUserAccess(access);
        }
        // Atualiza permissao dos usuarios colaboradores
        this.getCompanysUsers();
      }
    });
  }

  unsubscribeCompany() {
    if (this.observer) this.observer();
    this.reset();
  }

  async createOrUpdateCompany(name: string, file?: File) {
    const company = this._company;
    if (!company) {
      return CompanyController.createCompany(name, file);
    }
    return CompanyController.updateCompany(company, name, file);
  }

  async getCompanysUsers() {
    this.clearUsers();
    const usersPermissions = this.currentCompany?.users;
    if (!usersPermissions) return;

    const usersIds = Object.keys(usersPermissions);
    return UserService.getUsersByUid(usersIds)
      .then((users) => {
        this.setAllUsers(users, usersPermissions);
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(error.message);
      });
  }

  async createCompanysUser(name: string, email: string, cpf: string) {
    const company = this._company;
    const currentUser = UserStore.currentUser;
    if (!company || !currentUser) return;

    const data = {
      displayName: name,
      password: cpf,
      email,
      emailVerified: false,
      disabled: false,
      companyUid: company.uid,
      currentUser: currentUser.uid,
    };

    return UserService.createAcount(data)
      .then((response) => {
        if (response.data.message === "auth/user-created") {
          MessageStore.success("Usuário criado com sucesso!");
        } else if (response.data.message === "auth/email-already-exists") {
          MessageStore.error("Usuário não criado, e-mail ja cadastrado!");
        }
        return sleep(500);
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(`Não foi possível criar o colaborador!`);
      });
  }

  public async setCompanysUserAccess(uid: string, profileUid: string) {
    const employee = this.findUserByUid(uid);
    if (!employee) return;

    return UserService.updateUserRole(employee, profileUid).catch((error) => {
      MessageStore.error(
        lang("userProfile.unableToChangeEmployee", error.message)
      );
    });
  }

  async setCompanysUserStatus(uid: string) {
    const company = CompanyStore.currentCompany;
    const employee = this.findUserByUid(uid);
    if (!company || !employee) return;

    const oldValue = employee.disabled;
    const data = {
      id: employee.uid,
      data: {
        uid: employee.uid,
        email: employee.email,
        disabled: oldValue,
      },
    };

    return UserService.updateUserStatus(data)
      .then((response) => this.getCompanysUsers())
      .catch((error) => {
        console.error(error);
        MessageStore.error(
          `Não foi possível ${oldValue ? "ativar" : "desativar"} o colaborador!`
        );
      });
  }

  async deleteCompanysUser(uid: string) {
    const company = CompanyStore.currentCompany;
    const employee = this.findUserByUid(uid);
    if (!company || !employee) return;

    const data = {
      id: employee.uid,
      data: {
        uid: employee.uid,
        email: employee.email,
        companyUid: company.uid,
      },
    };

    return UserService.deleteUser(data)
      .then((response) => this.getCompanysUsers())
      .catch((error) => {
        console.error(error);
        MessageStore.error(`Não foi possível deletar o colaborador!`);
      });
  }

  private findUserByUid(uid: string) {
    return this._employeesList.find((user) => user.uid === uid);
  }

  private setAllUsers(users: Array<UserModel>, companyPermissions: IUsers) {
    if (!users) users = [];
    const currentUserId = UserStore.currentUser?.uid || "";
    const newUsers = users
      .filter((user) => currentUserId !== user.uid)
      .map((user) => {
        user.access = companyPermissions[user.uid];
        return user;
      })
      .sort((a, b) => compareStrings(a.name, b.name));

    this._employeesList = newUsers;
  }

  private clearUsers() {
    this._employeesList = [];
  }
}

const CompanyStore = new Store();
export default CompanyStore;
